import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useModals } from "@/context/ModalContext";
import { apiHistory } from "@/lib/namable";
import { History as HistoryIcon, ArrowRight, Clock } from "lucide-react";

export default function HistoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openAuth } = useModals();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    apiHistory()
      .then((d) => setSearches(d.searches || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-[560px] mx-auto px-4 sm:px-6 py-24 text-center">
        <Seo title="Search history — Namable" description="Your past name searches." path="/history" />
        <div className="w-14 h-14 rounded-[16px] bg-brand-soft text-brand flex items-center justify-center mx-auto">
          <HistoryIcon size={26} />
        </div>
        <h1 className="font-display text-[24px] font-extrabold text-ink mt-5">Keep your searches</h1>
        <p className="text-ink-muted text-[15px] mt-2">Sign up free to revisit every set of names you generate.</p>
        <Button className="mt-6 h-12 px-6 rounded-xl bg-brand hover:bg-brand-dark text-white font-bold btn-glow" onClick={() => openAuth({ mode: "register" })} data-testid="history-signup-btn">
          Create free account
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <Seo title="Search history — Namable" description="Your past name searches." path="/history" />
      <p className="overline text-brand">History</p>
      <h1 className="font-display text-[30px] font-extrabold tracking-[-0.02em] text-ink mt-1.5">Your searches</h1>

      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-7 h-7 rounded-full border-2 border-brand border-t-transparent animate-spin" />
        </div>
      ) : searches.length === 0 ? (
        <div className="card-soft p-10 text-center mt-8">
          <p className="text-ink-muted">No searches yet.</p>
          <Button className="mt-4 rounded-lg bg-brand text-white font-bold" onClick={() => navigate("/generate")}>
            Generate names
          </Button>
        </div>
      ) : (
        <div className="space-y-3 mt-7">
          {searches.map((s) => (
            <Link
              key={s.id}
              to={`/results/${s.id}`}
              className="card-soft p-5 flex items-center justify-between gap-4 hover:shadow-card transition-all group"
              data-testid={`history-item-${s.id}`}
            >
              <div className="min-w-0">
                <p className="text-[15px] font-semibold text-ink truncate">{s.description}</p>
                <p className="text-[12px] text-ink-subtle mt-1 flex items-center gap-1.5">
                  <Clock size={12} />{" "}
                  {s.engine_mode === "recommended"
                    ? "Recommended"
                    : `${(s.engines || []).length || 0} engine${(s.engines || []).length === 1 ? "" : "s"}`}{" "}
                  · {s.extension} · {(s.results?.length ?? s.count) || 0} names
                </p>
              </div>
              <ArrowRight size={18} className="text-ink-subtle group-hover:text-brand transition-colors shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
